import {
  createInfiniteQuery,
  type InfiniteQueryHookOptions,
} from 'react-query-kit';
import { type InfiniteData, keepPreviousData } from '@tanstack/react-query';

import {
  isRecoverableCursorError,
  useCursorQueryRecovery,
} from '@/shared/hooks/useCursorQueryRecovery';

import type { FolderSearchPage } from './fetchers/folder';
import { searchUserFolders } from './fetchers/userFolder';
import { folderKeys } from './folder.keys';
import type { MyFoldersVariables } from './folder.queries';

/** 남의 공개 폴더 검색 조건. 조건 모양은 내 폴더 검색과 같고 대상 사용자만 더한다. */
export type UserFoldersVariables = MyFoldersVariables & { userId: string };

type UserFoldersQueryOptions = InfiniteQueryHookOptions<
  FolderSearchPage,
  Error,
  InfiniteData<FolderSearchPage, UserFoldersPageParam>,
  UserFoldersVariables,
  UserFoldersPageParam
>;

/**
 * 첫 페이지는 `null`(커서 없음)이다. `undefined` 로 두면 SSR 이 `dehydratedState` 를 JSON 으로 만들 때
 * `pageParams[0]` 을 직렬화하지 못해 페이지가 500 이 된다(내 폴더 목록은 SSR 을 하지 않아 겪지 않았다).
 */
type UserFoldersPageParam = string | null;

/**
 * 원본 쿼리. 프로필 폴더 섹션(첫 페이지만)과 SSR prefetch 가 쓴다. 무한 목록 화면은 아래 `useUserFolders` 를 쓴다.
 */
export const useUserFoldersQuery = createInfiniteQuery<
  FolderSearchPage,
  UserFoldersVariables,
  Error,
  UserFoldersPageParam
>({
  queryKey: folderKeys.users(),
  fetcher: ({ userId, ...variables }, { pageParam, signal }) =>
    searchUserFolders(userId, variables, pageParam ?? undefined, signal),
  getNextPageParam: lastPage =>
    lastPage.hasNext ? lastPage.nextCursor : undefined,
  initialPageParam: null,
  placeholderData: keepPreviousData,
  throwOnError: error => !isRecoverableCursorError(error),
});

/** 남의 공개 폴더 전체 목록. 커서가 무효해지면 첫 페이지부터 한 번 다시 읽는다(`useMyFolders` 와 같다). */
export const useUserFolders = (options?: UserFoldersQueryOptions) => {
  const query =
    useUserFoldersQuery<InfiniteData<FolderSearchPage, UserFoldersPageParam>>(
      options,
    );
  const { isRecovering, shouldMaskError } = useCursorQueryRecovery({
    isError: query.isError,
    error: query.error,
    hasLoadedPages:
      !query.isPlaceholderData && (query.data?.pages.length ?? 0) > 0,
    refetch: query.refetch,
  });

  return {
    ...query,
    isError: query.isError && !shouldMaskError,
    isFetchingNextPage: query.isFetchingNextPage || isRecovering,
  };
};
